// external
import { createSelector } from 'reselect';

// libraries
import { SalesViewMode } from '@makemydeal/dr-shared-types';
import type { Dealer, DealerCreditTiers, TradeReconSettings } from '@makemydeal/dr-shared-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import {
    isBalloonDealTypeEnabled,
    isCarsActDisclosuresEnabled,
    isDealAssistantChatEnabled,
    isVppEditGuardEnabled
} from './featureToggleSelectors';
import type { ItemizedDiscount, OfferType, DealProduct } from '@makemydeal/dr-platform-types';
import { FINANCE } from '@makemydeal/dr-platform-types';
import { DEAL_SCREEN_EXPERIENCE } from '../utils/constants';

export const getDealerState = (state: StateTree): Partial<Dealer> => {
    return state.dealer || {};
};

export const isInventoryAssistantEnabled = (state: StateTree): boolean => getDealerState(state).inventoryAssistantEnabled === true;

export const isDealAssistantEnabled = (state: StateTree): boolean =>
    isDealAssistantChatEnabled(state) && isInventoryAssistantEnabled(state);

export const getSalesViewMode = (state: StateTree): SalesViewMode | undefined => {
    return getDealerState(state).salesViewMode;
};

export const getIsTraditionalMode = (state: StateTree): boolean => {
    return getSalesViewMode(state) === SalesViewMode.Showroom;
};

export const isSalesIntakeMode = (state: StateTree): boolean => {
    return getSalesViewMode(state) === SalesViewMode.SalesIntake;
};

export const getDealerEmail = (state: StateTree): string | undefined => {
    const dealer = getDealerState(state);
    return dealer.email;
};

export const getDealerName = (state: StateTree): string => {
    return getDealerState(state).name || '';
};

export const getDealerZip = (state: StateTree): string => {
    return getDealerState(state).address?.zip || '';
};

export const getDealerStateCode = (state: StateTree) => {
    return getDealerState(state).address?.state || '';
};

export const getDealerCity = (state: StateTree): string => {
    return getDealerState(state).address?.city || '';
};

export const getDealerStreet = (state: StateTree): string => {
    return getDealerState(state).address?.street || '';
};

export const getDealerTimeZoneId = (state: StateTree): string | undefined => {
    return getDealerState(state).address?.timeZoneId;
};

/**
 * @returns dealer ID or -1 if state isn't populated (this should never be the case)
 */
export const getDealerId = (state: StateTree): number => {
    const dealer = getDealerState(state);
    return dealer.dealerId || -1;
};

export const getVinSolutionsDealerId = (state: StateTree): number => {
    return getDealerState(state).vinSolutionsDealerId || -1;
};

/**
 * @returns phone number or '' if null or undefined.
 */
export const getDealerPhone = (state: StateTree): string => {
    const dealer = getDealerState(state);
    return dealer.phone || '';
};

export const isMenuFitToHeightEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isMenuFitToHeightEnabled || false;
};

export const isMenuPricingEnabled = (state: StateTree): boolean => {
    const isDealerMenuPricingEnabled = !state.dealer?.isMenuPricingDisabled;
    return isDealerMenuPricingEnabled;
};

export const isSwitchVehicleEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer?.enableSwitchVehicle || false;
};

export const isDeferredDownPaymentEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer?.enableDeferredDownPayment || false;
};

export const isVppEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isVppEnabled || false;
};

export const isVppEnabledForShopper = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isVppEnabledForShopper || false;
};

/**
 * For Dash this is hard-coded because there's just one theme (for now).
 */
export const getDealerSelectorsTheme = (_state: StateTree) => 'blue';

export const useDRCRM = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.useDRCRM || false;
};

export const isItemizedManufacturerIncentivesEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state) as Dealer;
    return dealer.enableItemizedManufacturerIncentives || false;
};

export const isCashOfferEnabled = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.cashOfferSettings?.hasCashOffer || false;
};

export const hasLeasing = (state: StateTree): boolean => {
    const leaseSettings = getDealerState(state).leaseSettings;
    return leaseSettings?.hasLeasing || false;
};

/**
 * Get the default lease quote type from dealer settings.
 * Returns 'MF' (Money Factor) or 'APR'.
 * Defaults to 'MF' if not set.
 */
export const getDefaultLeaseQuoteType = (state: StateTree): 'MF' | 'APR' => {
    const leaseSettings = getDealerState(state).leaseSettings;
    return leaseSettings?.defaultLeaseQuoteType || 'MF';
};

export const isTradeValuationKbb = (state: StateTree) => {
    const tradeProvider = state.dealer.tradeInValuationProvider;
    return tradeProvider?.toLocaleLowerCase() === 'kelley blue book';
};

export const getDealerTradeInValuationProvider = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.tradeInValuationProvider;
};

export const getLeadRoutingOverrideSettingsEnabled = (state: StateTree): boolean | undefined => {
    const dealer = getDealerState(state);
    return dealer.leadRoutingOverrideSettings?.leadRoutingOverrideEnabled;
};

export const getLeadOverrideDealerId = (state: StateTree): number | undefined => {
    const dealer = getDealerState(state);
    return dealer.leadRoutingOverrideSettings?.dealertrackOverrideId;
};

export const isDealerTradeInLeadFormForWebsiteEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer?.tradeInValuationLeadForm || false;
};

export const getListingOverride = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer?.listingOverride;
};

export const isDealerTradeInLeadFormForListingSiteEnabled = (state: StateTree): boolean => {
    const listingOverride = getListingOverride(state);
    return listingOverride?.tradeInValuationLeadForm || false;
};

export const hasTestDriveAtHome = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.hasTestDriveAtHome || false;
};

export const testDriveAtHomeOnTradeIn = (state: StateTree) => {
    return !!(hasTestDriveAtHome(state) && isDealerTradeInLeadFormForWebsiteEnabled(state));
};

export const isSubaruGtpEnabled = (state: StateTree): boolean => state.dealer?.isSubaruGtpEnabled || false;

// TODO: In future we should rename this to DueAtSigningAmountDeduct, it isn't at "sign in"!
export function getIsDueAtSigninAmountDeduct(state: StateTree): boolean {
    const { leaseSettings } = state.dealer;
    // lease cap cost due at signing flag
    // default value needs to be set to true in case this hasn't been implemented on the dealer.
    // False is a valid value though, so we only need to check for undefined or null in order to default.
    return leaseSettings &&
        leaseSettings.isDueAtSigninAmountDeduct !== undefined &&
        leaseSettings.isDueAtSigninAmountDeduct !== null
        ? leaseSettings.isDueAtSigninAmountDeduct
        : true;
}

export const getOwnerId = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.ownerId;
};

export const getIsNewVehicleSinglePrice = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.isNewVehicleSinglePrice;
};

export const getIsUsedVehicleSinglePrice = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.isUsedVehicleSinglePrice;
};

export const hasNoTradeOwnership = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.hasNoTradeOwnership || false;
};

export const isDealertrackCreditProvider = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isDealertrackCreditProvider || false;
};

export const getCreditProvider = (state: StateTree): string => {
    const dealer = getDealerState(state);
    let creditProviderInitialValue;
    if (!dealer.creditProvider || (dealer.creditProvider && dealer.creditProvider === '')) {
        creditProviderInitialValue = isDealertrackCreditProvider(state) ? 'dealertrack' : 'external';
    }
    return dealer.creditProvider || (creditProviderInitialValue as string);
};

export const getDMSIntegrationToggle = (state: StateTree): string => {
    const dealer = getDealerState(state);
    return dealer.dmsIntegration || 'OFF';
};

export const getTradeInHideExcellentConditionWebsite = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.tradeInHideExcellent || false;
};

export const getTradeInHideExcellentConditionListingSite = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.listingOverride?.tradeInHideExcellent || false;
};

export const getCreditTiers = createSelector(
    getDealerState,
    (dealer): DealerCreditTiers => dealer.creditTiers || ({} as DealerCreditTiers)
);

export const isAccessoriesCatalogEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isAccessoriesCatalogEnabled || false;
};

export const isContinueToDeskingDisabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isContinueToDeskingDisabled || false;
};

export const isVAutoAppraisalEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer?.enableVauto || false;
};

export const getIsCudlIntegrationEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer?.enableCudlIntegration || false;
};

export const getDealPulseToggle = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.showDealPulseInManagerView || false;
};

export const getCrmIntegrationToggle = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.isCrmIntegrationEnabled || false;
};

export const isCreditProviderRouteOne = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.creditProvider === 'r1_dealertrack';
};

export const enableCheckoutAnywhereToggle = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.enableCheckoutAnywhereInMV || false;
};

export const getDealerTextAddress = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.address?.address;
};

export const getRouteOnePartyId = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.routeOnePartyId || '';
};

export const getCudlPartyId = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.cudlPartyId || '';
};

export const isDarwinEnabled = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.enableDarwin || false;
};

export const isDarwinVppEditGuardEnabled = (state: StateTree): boolean => isVppEditGuardEnabled(state) && isDarwinEnabled(state);

export const getIsTestDealer = (state: StateTree) => {
    const dealer = getDealerState(state);
    return dealer.isTestDealer || false;
};

export const getDealerBrandingExperienceLogo = (state: StateTree): string => {
    const dealer = getDealerState(state);
    return dealer.brandingExperienceLogo || '';
};

export const enableDigitalDealJacket = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return !!dealer.enableDigitalDealJacket;
};

export const isBaseDealExperience = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.dealScreenExperience === DEAL_SCREEN_EXPERIENCE.BASE;
};
export const isDealCentralExperience = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return (
        dealer.dealScreenExperience === DEAL_SCREEN_EXPERIENCE.DEAL_CENTRAL ||
        dealer.dealScreenExperience === DEAL_SCREEN_EXPERIENCE.DEAL_CENTRAL_WITH_FNI ||
        dealer.dealScreenExperience === DEAL_SCREEN_EXPERIENCE.MV_LITE
    );
};

export const isDealCentralWithFnI = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.dealScreenExperience === DEAL_SCREEN_EXPERIENCE.DEAL_CENTRAL_WITH_FNI;
};

export const isMvLiteEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.dealScreenExperience === DEAL_SCREEN_EXPERIENCE.MV_LITE;
};

export const getEnableGenerateContracts = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.enableGenerateContracts || false;
};

export const isShowPaymentRangeEnabled = (state: StateTree): boolean => {
    const { showPaymentRange } = getDealerState(state);
    return showPaymentRange || false;
};

export const getDealerPaymentRangeByOfferType = (state: StateTree, offerType: OfferType): number => {
    const dealer = getDealerState(state);
    switch (offerType) {
        case FINANCE:
            return dealer.financeRangeAmount || 0;
        default:
            return dealer.leaseRangeAmount || 0;
    }
};

export const isDealCommentsMVEnabled = (state: StateTree): boolean => {
    const { enableDealCommentsMV } = getDealerState(state);
    return !!enableDealCommentsMV;
};

export const dealerFlexBuyEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.enableFordFlexBuy || false;
};

export const getPartnerId = (state: StateTree): string | undefined => {
    const { partnerId } = getDealerState(state);
    return partnerId;
};

export const getPartnerDealerId = (state: StateTree): number | undefined => {
    const { partnerDealerId } = getDealerState(state);

    return partnerDealerId ? +partnerDealerId : undefined;
};

export const isDealAuditLog = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.enableDealAuditLog || false;
};

export const isEnableDealUrlForPrintMVEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return !!dealer.enableDealUrlForPrintMV;
};

export const isSpdManagementEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.spdManagement || false;
};

export const getAdminDealerDiscounts = (state: StateTree): ItemizedDiscount[] => {
    const dealer = getDealerState(state);
    return dealer.discounts || [];
};

export const getDealerLogo = (state: StateTree): string | undefined => {
    const dealer = getDealerState(state);
    return dealer.dealerLogo;
};

const EMPTY_ARRAY: DealProduct[] = [];

export const getManualVppProducts = createSelector([getDealerState], (dealer): DealProduct[] => dealer.manualVpp || EMPTY_ARRAY);

export const getManualAccessories = createSelector([getDealerState], (dealer) => dealer.manualAccessories || EMPTY_ARRAY);

export const isCustomerViewExperience = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.enableNonAMDCustomerExperience || false;
};

export const isAmdEnabledDealer = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return !!dealer.productSet?.accelerateMyDeal;
};

export const isDealerAISearchEnabled = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.aiSearchSettings?.enabled || false;
};

export const useLockRestrictedDeals = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.lockRestrictedDeals || false;
};

export const isTradeAcvOverrideEnabled = (_state: StateTree): boolean => true;

export const isBalloonDealTypeEnabledForDealer = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return isBalloonDealTypeEnabled(state) && !!dealer.enableBalloonDealType;
};

/**
 * States whose consumer-protection statutes require the Tot. Amt you will pay / Est. Total
 * Amount You Will Pay disclosure. Currently only California.
 */
const CARS_ACT_DISCLOSURE_STATES: Partial<Record<string, boolean>> = {
    CA: true
};

export const isCarsActDisclosuresApplicable = (state: StateTree): boolean => {
    const dealerStateCode = getDealerStateCode(state);
    return isCarsActDisclosuresEnabled(state) && CARS_ACT_DISCLOSURE_STATES[dealerStateCode] === true;
};

export const isItemizedDiscountsEnabledForDealer = (state: StateTree): boolean => {
    const dealer = getDealerState(state);
    return dealer.enableItemizedDiscounts ?? true;
};

export const getTradeReconSettings = (state: StateTree): TradeReconSettings => {
    const dealer = getDealerState(state);
    return dealer.tradeReconSettings ?? ({} as TradeReconSettings);
};

export const getDealerPreferredBookSource = (state: StateTree): string => {
    return getDealerState(state).preferredBookSource || '';
};

export const getDealerPreferredBookoutType = (state: StateTree): string => {
    return getDealerState(state).preferredBookoutType || '';
};
